<template>
  <div>
    <breadcrumb :items="breadcrumbs" />

    <v-container fluid>
      <!-- Page header (matches UserDetails layout) -->
      <div class="d-flex align-center justify-space-between mt-6 mb-4">
        <h2 class="font-weight-bold mb-6">
          {{ $t('finance.loanApplications.loanApplications') }}
        </h2>
        <div>
          <v-btn
            outlined
            class="mr-2 mb-2"
            :to="backButtonTo"
            style="border-color: #00BD73; color: #00BD73; background-color: white;"
          >
            {{ $t('finance.loanApplications.back') }}
          </v-btn>

          <!-- Bank user: Request Additional Info, Reject, Approval Process (pending / sent_to_bank / waiting_signatures) -->
          <template v-if="isBankUser && bankEligibleStatusForActions">
            <v-btn
              class="mr-2 mb-2"
              color="primary"
              dark
              @click="showRequestAdditionalInfoDialog = true"
            >
              {{ $t('finance.loanApplications.requestAdditionalInformation') }}
            </v-btn>
            <v-btn
              class="mr-2 mb-2"
              color="#F03737"
              dark
              @click="showRejectDialog = true"
            >
              {{ $t('finance.loanApplications.reject') }}
            </v-btn>
            <v-btn
              class="mr-2 mb-2"
              color="primary"
              dark
              @click="showApprovalProcessDialog = true"
            >
              {{ approvalActionButtonText }}
            </v-btn>
          </template>
          <!-- Cooperative only: Request Additional Info, Reject, Send To Bank. Bank never sees Send To Bank (does Approval Process only). -->
          <template v-else-if="!isBankUser && !['sent_to_bank', 'approved', 'rejected', 'waiting_signatures'].includes(application.status)">
            <v-btn
              class="mr-2 mb-2"
              color="primary"
              dark
              @click="showRequestAdditionalInfoDialog = true"
            >
              {{ $t('finance.loanApplications.requestAdditionalInformation') }}
            </v-btn>

            <v-btn
              class="mr-2 mb-2"
              color="#F03737"
              dark
              @click="showRejectDialog = true"
            >
              {{ $t('finance.loanApplications.reject') }}
            </v-btn>

            <v-btn
              class="mr-2 mb-2"
              color="primary"
              dark
              @click="showSendToBankDialog = true"
            >
              {{ $t('finance.loanApplications.sendToBank') }}
            </v-btn>
          </template>
        </div>
      </div>

      <!-- Additional Information Requested / Farmer Updates (above main card, NewWarningBox-style) -->
      <v-card
        v-if="additionalInfoEntries.length > 0"
        outlined
        class="pa-2 my-4 additional-info-card"
      >
        <v-row class="additional-info-header" align="center" @click="additionalInfoSectionExpanded = !additionalInfoSectionExpanded">
          <v-col cols="auto" class="mx-3">
            <h2 class="additional-info-heading">
              {{ $t('finance.loanApplications.information') }}
              <v-tooltip top color="primary" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon class="ml-2 primary--text additional-info-tooltip-icon" v-bind="attrs" v-on="on">
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ $t('finance.loanApplications.thisSectionShowsAdditionalInformationRequestsSentToTheFarmerAndTheirResponsesYouWillBeNotifiedWhenTheFarmerUpdatesTheApplication') }}</span>
              </v-tooltip>
            </h2>
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <v-btn icon>
              <v-icon>{{ additionalInfoSectionExpanded ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-expand-transition>
          <div v-show="additionalInfoSectionExpanded" class="additional-info-body">
            <div
              v-for="(entry, index) in additionalInfoEntries"
              :key="'entry-' + index"
              class="additional-info-entry"
            >
              <p class="mb-2">
                <span class="additional-info-entry-title">{{ entry.title }} ({{ entry.date }})</span>
              </p>
              <p class="additional-info-entry-text">{{ entry.message }}</p>
            </div>
          </div>
        </v-expand-transition>
      </v-card>

      <!-- 1. Applicant details -->
      <v-card flat class="pa-6 mb-6">
        <!-- Key application info -->
        <v-row class="mb-2">
          <v-col cols="12" md="3">
            <div class="view-block">
              <div class="d-flex align-center">
                <label class="view-label">{{ $t('finance.loanApplications.applicationId') }}</label>
                <v-tooltip top color="#00BD73" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      size="20"
                      style="color: #00BD73; cursor: pointer;"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ $t('finance.loanApplications.applicationId') }}</span>
                </v-tooltip>
              </div>
              <div class="view-value">{{ application.applicationId }}</div>
            </div>
          </v-col>

          <v-col cols="12" md="3">
            <div class="view-block">
              <div class="d-flex align-center">
                <label class="view-label">{{ $t('finance.loanApplications.submissionDate') }}</label>
                <v-tooltip top color="#00BD73" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      size="20"
                      style="color: #00BD73; cursor: pointer;"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ $t('finance.loanApplications.submissionDate') }}</span>
                </v-tooltip>
              </div>
              <div class="view-value">{{ application.submissionDate }}</div>
            </div>
          </v-col>

          <v-col cols="12" md="3">
            <div class="view-block">
              <div class="d-flex align-center">
                <label class="view-label">{{ $t('finance.loanApplications.loanProgram') }}</label>
                <v-tooltip top color="#00BD73" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      size="20"
                      style="color: #00BD73; cursor: pointer;"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ $t('finance.loanApplications.loanProgram') }}</span>
                </v-tooltip>
              </div>
              <div class="view-value">{{ application.programName }}</div>
            </div>
          </v-col>

          <v-col v-if="showTopLoanAmount" cols="12" md="3">
            <div class="view-block">
              <div class="d-flex align-center">
                <label class="view-label">{{ $t('finance.loanApplications.loanAmount') }}</label>
                <v-tooltip top color="#00BD73" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      size="20"
                      style="color: #00BD73; cursor: pointer;"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ $t('finance.loanApplications.loanAmount') }}</span>
                </v-tooltip>
              </div>
              <div class="view-value">{{ application.loanAmountFormatted || '—' }}</div>
            </div>
          </v-col>

          <v-col v-if="!showTopLoanAmount" cols="12" md="3">
            <div class="view-block">
              <div class="d-flex align-center">
                <label class="view-label">{{ $t('finance.loanApplications.status') }}</label>
                <v-tooltip top color="#00BD73" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      size="20"
                      style="color: #00BD73; cursor: pointer;"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ $t('finance.loanApplications.status') }}</span>
                </v-tooltip>
              </div>
              <div class="mt-2">
                <div
                  class="info-status-pill status-pill-fixed"
                  :class="detailsStatusClass"
                >
                  <span class="info-status-text">
                    {{ statusText }}
                  </span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row v-if="showTopLoanAmount" class="mb-6">
          <v-col cols="12" md="3">
            <div class="view-block">
              <div class="d-flex align-center">
                <label class="view-label">{{ $t('finance.loanApplications.status') }}</label>
                <v-tooltip top color="#00BD73" max-width="300">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      size="20"
                      style="color: #00BD73; cursor: pointer;"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ $t('finance.loanApplications.status') }}</span>
                </v-tooltip>
              </div>
              <div class="mt-2">
                <div
                  class="info-status-pill status-pill-fixed"
                  :class="detailsStatusClass"
                >
                  <span class="info-status-text">
                    {{ statusText }}
                  </span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <!-- User Details -->
        <div class="mb-6">
          <div class="d-flex align-center mb-4">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.userDetails') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="20"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.userDetails') }}</span>
            </v-tooltip>
          </div>
          <v-row>
            <v-col cols="12" md="3">
              <div class="view-block">
                <div class="d-flex align-center">
                  <label class="view-label">{{ $t('finance.loanApplications.firstName') }}</label>
                  <v-tooltip top color="#00BD73" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="ml-2"
                        size="20"
                        style="color: #00BD73; cursor: pointer;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('finance.loanApplications.firstName') }}</span>
                  </v-tooltip>
                </div>
                <div class="view-value">{{ application.firstName }}</div>
              </div>
            </v-col>

            <v-col cols="12" md="3">
              <div class="view-block">
                <div class="d-flex align-center">
                  <label class="view-label">{{ $t('finance.loanApplications.lastName') }}</label>
                  <v-tooltip top color="#00BD73" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="ml-2"
                        size="20"
                        style="color: #00BD73; cursor: pointer;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('finance.loanApplications.lastName') }}</span>
                  </v-tooltip>
                </div>
                <div class="view-value">{{ application.lastName }}</div>
              </div>
            </v-col>

            <v-col cols="12" md="3">
              <div class="view-block">
                <div class="d-flex align-center">
                  <label class="view-label">{{ $t('finance.loanApplications.email') }}</label>
                  <v-tooltip top color="#00BD73" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="ml-2"
                        size="20"
                        style="color: #00BD73; cursor: pointer;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('finance.loanApplications.email') }}</span>
                  </v-tooltip>
                </div>
                <div class="view-value">{{ application.applicantEmail }}</div>
              </div>
            </v-col>

            <v-col cols="12" md="3">
              <div class="view-block">
                <div class="d-flex align-center">
                  <label class="view-label">{{ $t('finance.loanApplications.phoneNumber') }}</label>
                  <v-tooltip top color="#00BD73" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="ml-2"
                        size="20"
                        style="color: #00BD73; cursor: pointer;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('finance.loanApplications.phoneNumber') }}</span>
                  </v-tooltip>
                </div>
                <div class="view-value">{{ application.phoneNumber }}</div>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-6" />

        <!-- Farm Details -->
        <div class="mb-6">
          <div class="d-flex align-center mb-4">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.farmDetails') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="20"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.farmDetails') }}</span>
            </v-tooltip>
          </div>
          <v-row>
            <!-- Text details (3 columns * 2 rows) -->
            <v-col cols="12" md="9">
              <v-row>
                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.farm') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.farm') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.farmName }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.area') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.area') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.area }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.provinceState') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.provinceState') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.provinceState }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.regionDistrict') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.regionDistrict') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.regionDistrict }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.villageTownCity') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.villageTownCity') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.villageTownCity }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <div class="view-block">
                    <div class="d-flex align-center">
                      <label class="view-label">{{ $t('finance.loanApplications.address') }}</label>
                      <v-tooltip top color="#00BD73" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            class="ml-2"
                            size="20"
                            style="color: #00BD73; cursor: pointer;"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t('finance.loanApplications.address') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="view-value">{{ application.address }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <!-- Map column: native Google Maps (same loader as farm ViewGeoLocation) -->
            <v-col cols="12" md="3">
              <div
                class="view-block map-wrapper"
                @click="showMapDialog = true"
              >
                <div
                  v-if="hasFarmMapCoords"
                  ref="farmPreviewMap"
                  class="farm-preview-google-map"
                />
                <div
                  v-else
                  class="farm-preview-google-map farm-preview-google-map--empty d-flex align-center justify-center text-caption grey--text"
                >
                  {{ $t('finance.loanApplications.noMapCoordinatesForThisFarm') }}
                </div>
                <div class="map-caption mt-2 d-flex align-center">
                  <span class="map-text">
                    {{ $t('finance.loanApplications.tapTheMapToExpandIt') }}
                  </span>
                  <v-icon small class="ml-1">mdi-magnify-plus-outline</v-icon>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>


        <v-divider class="my-6" />

        <!-- Additional Questions -->
        <div class="mb-8">
          <div class="d-flex align-center mb-2">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.additionalQuestions') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="18"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.additionalLoanRelatedQuestionsAndSupportingDocuments') }}</span>
            </v-tooltip>
          </div>

          <div v-if="!additionalQuestions.length" class="text-body-2 grey--text">
            {{ $t('finance.loanApplications.noAdditionalQuestions') }}
          </div>
          <div
            v-for="(qa, idx) in additionalQuestions"
            :key="qa.id || 'aq-' + idx"
            class="mb-4"
          >
            <div class="font-weight-bold mb-1">
              {{ (qa.formField && qa.formField.fieldName) || $t('finance.loanApplications.question') }}
            </div>
            <div
              v-if="qa.formField && qa.formField.helpText"
              class="text-caption grey--text mb-2"
            >
              {{ qa.formField.helpText }}
            </div>
            <div v-if="isAdditionalQuestionAttachment(qa)" class="d-flex flex-wrap">
              <a
                :href="qa.response"
                target="_blank"
                rel="noopener noreferrer"
                class="attachment-card text-decoration-none"
              >
                <div class="attachment-icon">
                  <span class="attachment-icon-badge">{{ additionalQuestionFileBadge(qa.response) }}</span>
                  <v-icon small color="#135B00" class="attachment-download">mdi-open-in-new</v-icon>
                </div>
                <div class="attachment-label">{{ $t('finance.loanApplications.openFile') }}</div>
              </a>
            </div>
            <div v-else class="text-body-2">
              <span v-if="Array.isArray(qa.response)">{{ qa.response.join(", ") }}</span>
              <span v-else>{{ qa.response != null && qa.response !== '' ? qa.response : '—' }}</span>
            </div>
          </div>
        </div>
      </v-card>

      <!-- Farm Reports -->
      <!-- <v-card flat class="pa-6 mb-6">
         <div class="mb-6">
          <div class="d-flex align-center mb-2">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.farmReports') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="18"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.someDisclaimerOrShortExplainerAboutFollowingReports') }}</span>
            </v-tooltip>
          </div>
          <div class="text-body-2 mb-4">
            {{ $t('finance.loanApplications.someDisclaimerOrShortExplainerAboutFollowingReports') }}
          </div>
          <v-row class="farm-reports-row" no-gutters>
            
            <v-col cols="12" md="3" class="px-2">
              <div class="report-card">
                <div class="d-flex align-center mb-1">
                  <span class="font-weight-bold mr-1">
                    {{ $t('finance.loanApplications.yieldPredictionReport') }}
                  </span>
                  <v-tooltip top color="#00BD73" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        style="color: #00BD73; cursor: pointer;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('finance.loanApplications.projectedYieldComparisonByYear') }}</span>
                  </v-tooltip>
                </div>
                <v-row class="mt-2" no-gutters>
                  <v-col cols="7">
                    <div class="text-body-2">
                      <div class="mb-1">
                        <span class="font-weight-bold">{{ $t('finance.loanApplications.year2025') }}:</span>
                        2977 kg/ha
                      </div>
                      <div>
                        <span class="font-weight-bold">{{ $t('finance.loanApplications.year2026') }}:</span>
                        2942 kg/ha
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="5">
                    <div class="d-flex align-center justify-start">
                      <v-icon :color="yieldChangeColor" large class="mr-2">
                        {{ yieldChangeIcon }}
                      </v-icon>
                      <span class="font-weight-bold" style="color:#808080;">
                        {{ yieldChangeDisplay }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
                <v-btn
                  small
                  outlined
                  color="#00BD73"
                  class="mt-4 text-uppercase"
                  @click="openYieldPredictionReport"
                >
                  {{ $t('finance.loanApplications.viewFullReport') }}
                </v-btn>
              </div>
            </v-col>

            
            <v-col cols="12" md="3" class="px-2">
              <div class="report-card">
                <div class="d-flex align-center mb-1">
                  <span class="font-weight-bold mr-1">
                    {{ $t('finance.loanApplications.landSuitabilityReport') }}
                  </span>
                  <v-tooltip top color="#00BD73" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        style="color: #00BD73; cursor: pointer;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('finance.loanApplications.overallLandSuitabilityScoreAndCategory') }}</span>
                  </v-tooltip>
                </div>
                <v-row class="mt-2" no-gutters>
                  <v-col cols="7">
                    <div class="text-body-2">
                      <span class="font-weight-bold">
                        {{ $t('finance.loanApplications.overallScoreForLandSuitability') }}:
                      </span>
                      79.57 %
                    </div>
                  </v-col>
                  <v-col cols="5" class="d-flex align-center justify-start">
                    <div class="d-flex align-center">
                      <v-icon
                        color="#FACC15"
                        class="mr-2"
                      >
                        mdi-square
                      </v-icon>
                      <span class="font-weight-bold" style="color:#808080;">
                        {{ $t('finance.loanApplications.moderate') }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
                <v-btn
                  small
                  outlined
                  color="#00BD73"
                  class="mt-4 text-uppercase"
                  @click="openFarmReport('land')"
                >
                  {{ $t('finance.loanApplications.viewFullReport') }}
                </v-btn>
              </div>
            </v-col>

            
            <v-col cols="12" md="4" class="px-2">
              <div class="report-card">
                <div class="d-flex align-center mb-1">
                  <span class="font-weight-bold mr-1">
                    {{ $t('finance.loanApplications.soilAnalysisReport') }}
                  </span>
                  <v-tooltip top color="#00BD73" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        style="color: #00BD73; cursor: pointer;"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-alert-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('finance.loanApplications.keySoilNutrientLevelsForTheFarm') }}</span>
                  </v-tooltip>
                </div>
                <div class="text-body-2 mt-2">
                  <div class="mb-1">
                    <span class="font-weight-bold">
                      {{ $t('finance.loanApplications.nitrogenContent') }}:
                    </span>
                    60 Kg N/hectare
                  </div>
                  <div class="mb-1">
                    <span class="font-weight-bold">
                      {{ $t('finance.loanApplications.phosphorusContent') }}:
                    </span>
                    100 Kg (P2O5) / hectare
                  </div>
                  <div>
                    <span class="font-weight-bold">
                      {{ $t('finance.loanApplications.potassiumContent') }}:
                    </span>
                    85 Kg K2O / hectare
                  </div>
                </div>
                <v-btn
                  small
                  outlined
                  color="#00BD73"
                  class="mt-4 text-uppercase"
                  @click="openFarmReport('soil')"
                >
                  {{ $t('finance.loanApplications.viewFullReport') }}
                </v-btn>
              </div>
            </v-col>

            <v-col cols="12" md="2" class="px-2"></v-col>
          </v-row>
        </div>
      </v-card> -->

      <!-- Additional Information Requested (separate v-card when additional requested info is available) -->
      <v-card
          v-if="showAdditionalInfoCard"
          flat
          outlined
          class="pa-6 mb-6 additional-info-requested-card"
        >
          <div class="d-flex align-center mb-4">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.additionalInformationRequested') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="18"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.thisSectionShowsAdditionalInformationRequestsSentToTheFarmerAndTheirResponses') }}</span>
            </v-tooltip>
          </div>

          <div class="mb-4">
            <div class="font-weight-bold mb-1 additional-info-requested-subheading">
              {{ $t('finance.loanApplications.informationRequestedFromBank') }} - ({{ additionalInfoRequestDisplayDateTime }})
            </div>
            <div class="text-body-2">{{ additionalInfoRequestForCard?.message || '—' }}</div>
          </div>

          <div v-if="farmerResponseForCard" class="mb-4">
            <div class="font-weight-bold mb-1 additional-info-requested-subheading">
              {{ $t('finance.loanApplications.farmerResponse') }} - ({{ farmerResponseDisplayDateTime }})
            </div>
            <div class="text-body-2">{{ farmerResponseForCard?.message || '—' }}</div>
          </div>

          <v-row v-if="farmerResponseAttachments.length > 0">
            <v-col cols="12" md="2" v-for="(att, n) in farmerResponseAttachments" :key="'resp-att-' + n">
              <a
                v-if="farmerAttachmentUrl(att)"
                :href="farmerAttachmentUrl(att)"
                target="_blank"
                rel="noopener noreferrer"
                class="attachment-card text-decoration-none"
              >
                <div class="attachment-icon">
                  <span class="attachment-icon-badge">{{ att.fileType || farmerAttachmentBadge(att) }}</span>
                  <v-icon small color="#135B00" class="attachment-download">mdi-open-in-new</v-icon>
                </div>
                <div class="attachment-label">{{ $t('finance.loanApplications.attachment') }} {{ n + 1 }}</div>
              </a>
              <div v-else class="attachment-card">
                <div class="attachment-icon">
                  <span class="attachment-icon-badge">{{ att.fileType || farmerAttachmentBadge(att) }}</span>
                  <v-icon small color="#135B00" class="attachment-download">mdi-download</v-icon>
                </div>
                <div class="attachment-label">{{ $t('finance.loanApplications.attachment') }} {{ n + 1 }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      <!-- 2a. Contract (waiting_signatures / approved: single Contract section, bank file + signed contracts with Digitally Signed / Signed offline) -->
      <v-card v-if="showContractFromBankAndApplicantView" flat class="pa-6 mb-6">
        <div class="font-weight-bold mb-3">{{ $t('finance.loanApplications.contract') }}</div>
        <FinalizeContract
          :readonly="true"
          :display-as-contract-section="true"
          :hide-title-in-display="true"
          :sign-in-app.sync="signInApp"
          :contract-file.sync="uploadedContractFile"
          :contract-s3-link="loanDetails.contractS3Link"
        />
        <v-row v-if="application.status === 'approved' && mobileUserSignedContracts.length > 0" class="mt-4">
          <v-col
            v-for="(item, index) in mobileUserSignedContracts"
            :key="'mobile-contract-' + index"
            cols="12"
            md="3"
          >
            <div class="attachment-card contract-uploaded-card">
              <div class="attachment-icon">
                <span class="attachment-icon-badge">PDF</span>
                <v-icon small color="#135B00" class="attachment-download">mdi-download</v-icon>
              </div>
              <div class="attachment-label">{{ item.name || $t('finance.loanApplications.attachment') + ' ' + (index + 1) }}</div>
              <div class="text-caption mt-1" :class="item.signedDigitally ? 'success--text' : 'grey--text darken-1'">
                {{ item.signedDigitally ? $t('finance.loanApplications.digitallySigned') : $t('finance.loanApplications.signedOffline') }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- 2b. Finalize Contract (Bank user + Sent to Bank only, after approval confirmation) -->
      <v-card v-if="showFinalizeContract" flat class="pa-6 mb-6">
        <FinalizeContract
          :readonly="!canBankEditDetails"
          :sign-in-app.sync="signInApp"
          :contract-file.sync="uploadedContractFile"
        />
      </v-card>

      <!-- 3. Loan details (form + plans or summary-only for waiting_signatures / approved) -->
      <v-card v-if="showLoanDetailsFormCard" flat class="pa-6 mb-6">
        <LoanDetails
          section="form"
          :summary-only="showWaitingSignaturesView || application.status === 'approved'"
          :readonly="showWaitingSignaturesView || application.status === 'approved' || !canEditLoanDetails"
          :loan-details="loanDetails"
          :loan-details-summary="loanDetailsSummary"
          :interest-rate-unit-options="interestRateUnitOptions"
          :grace-period-unit-options="gracePeriodUnitOptions"
          :grace-period-type-options="gracePeriodTypeOptions"
          :disbursement-file.sync="uploadedDisbursementFile"
          :repayment-file.sync="uploadedRepaymentFile"
          :disbursement-file-ext="disbursementFileExt"
          :repayment-file-ext="repaymentFileExt"
          :disbursement-schedule-generated="disbursementScheduleGenerated"
          :repayment-schedule-generated="repaymentScheduleGenerated"
          :filtered-disbursement-schedule="filteredDisbursementSchedule"
          :filtered-repayment-schedule="filteredRepaymentSchedule"
          :paginated-repayment-schedule="paginatedRepaymentSchedule"
          :repayment-schedule-total-pages="repaymentScheduleTotalPages"
          :disbursement-search.sync="disbursementSearch"
          :disbursement-status-filter.sync="disbursementStatusFilter"
          :repayment-search.sync="repaymentSearch"
          :repayment-status-filter.sync="repaymentStatusFilter"
          :repayment-schedule-page.sync="repaymentSchedulePage"
          :repayment-schedule-per-page="repaymentSchedulePerPage"
          :schedule-status-options="scheduleStatusOptions"
          :disbursement-table-headers="disbursementTableHeaders"
          :repayment-table-headers="repaymentTableHeaders"
          @generate-disbursement="generateDisbursementSchedule"
          @generate-repayment="generateRepaymentSchedule"
        />
      </v-card>

      <!-- 4. Loan tables (disbursement & repayment schedules) - data only when waiting_signatures / approved -->
      <v-card v-if="showLoanDetailsTablesCard" flat class="pa-6 mb-6">
        <LoanDetails
          section="tables"
          :readonly="showWaitingSignaturesView || application.status === 'approved' || !canEditLoanDetails"
          :loan-details="loanDetails"
          :loan-details-summary="loanDetailsSummary"
          :interest-rate-unit-options="interestRateUnitOptions"
          :grace-period-unit-options="gracePeriodUnitOptions"
          :grace-period-type-options="gracePeriodTypeOptions"
          :disbursement-file.sync="uploadedDisbursementFile"
          :repayment-file.sync="uploadedRepaymentFile"
          :disbursement-file-ext="disbursementFileExt"
          :repayment-file-ext="repaymentFileExt"
          :disbursement-schedule-generated="disbursementScheduleGenerated"
          :repayment-schedule-generated="repaymentScheduleGenerated"
          :filtered-disbursement-schedule="filteredDisbursementSchedule"
          :filtered-repayment-schedule="filteredRepaymentSchedule"
          :paginated-repayment-schedule="paginatedRepaymentSchedule"
          :repayment-schedule-total-pages="repaymentScheduleTotalPages"
          :disbursement-search.sync="disbursementSearch"
          :disbursement-status-filter.sync="disbursementStatusFilter"
          :repayment-search.sync="repaymentSearch"
          :repayment-status-filter.sync="repaymentStatusFilter"
          :repayment-schedule-page.sync="repaymentSchedulePage"
          :repayment-schedule-per-page.sync="repaymentSchedulePerPage"
          :schedule-status-options="scheduleStatusOptions"
          :disbursement-table-headers="disbursementTableHeaders"
          :repayment-table-headers="repaymentTableHeaders"
          @generate-disbursement="generateDisbursementSchedule"
          @generate-repayment="generateRepaymentSchedule"
        />
      </v-card>

      <!-- 5. Notes -->
      <v-card flat class="pa-6 mb-6">
        <div class="mb-8">
          <div class="d-flex align-center mb-2">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.notes') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="18"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.addAnyInternalNotesOrReviewCommentsAboutThisLoanApplication') }}</span>
            </v-tooltip>
          </div>
          <div class="mb-1">
            <span class="view-label">{{ $t('finance.loanApplications.notes') }}</span>
          </div>
          <v-textarea
            outlined
            auto-grow
            rows="3"
            :placeholder="$t('finance.loanApplications.describeHere')"
          />
        </div>
      </v-card>

      <!-- 6. Activity Log -->
      <v-card flat class="pa-6 mb-6">
        <div class="mb-8">
          <div class="d-flex align-center mb-2">
            <h3 class="font-weight-bold mr-2">
              {{ $t('finance.loanApplications.activityLog') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="ml-1"
                  size="18"
                  style="color: #00BD73; cursor: pointer;"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.historyOfActionsTakenOnThisLoanApplication') }}</span>
            </v-tooltip>
          </div>

          <v-row class="mb-4 d-flex align-center">
            <v-col cols="12" md="4" class="activity-log-filter-field">
              <label class="view-label mb-1">{{ $t('finance.loanApplications.user') }}</label>
              <v-text-field
                v-model="activitySearch"
                outlined
                dense
                prepend-inner-icon="mdi-magnify"
                :placeholder="$t('finance.loanApplications.search')"
              />
            </v-col>
            <v-col cols="12" md="4" class="activity-log-filter-field">
              <label class="view-label mb-1">{{ $t('finance.loanApplications.userRole') }}</label>
              <v-select
                v-model="activityRoleFilter"
                :items="activityRoleOptions"
                outlined
                dense
                :placeholder="$t('finance.loanApplications.select')"
              />
            </v-col>
          </v-row>

          <v-data-table
            :headers="activityLogHeaders"
            :items="filteredActivityLog"
            class="activity-log-table"
            hide-default-footer
          >
            <template v-slot:item="{ item }">
              <tr :class="item.highlight ? 'activity-log-row--highlight' : ''">
                <td>{{ item.user }}</td>
                <td>{{ item.role }}</td>
                <td>{{ item.ip }}</td>
                <td>{{ item.dateTime }}</td>
                <td>{{ item.description }}</td>
                <td>{{ item.activityDescription }}</td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-container>

    <LocationDialog
      v-model="showMapDialog"
      :farm-name="application.farmName"
      :farmer-name="application.applicantName"
      :country="(application.applicantFarm && application.applicantFarm.country) || application.provinceState"
      :area-ha="application.area"
      :initial-latitude="mapLat"
      :initial-longitude="mapLng"
    />

    <!-- Request Additional Information dialog -->
    <ConfirmationDialog
      v-model="showRequestAdditionalInfoDialog"
      :title="$t('finance.loanApplications.requestAdditionalInformation')"
      header-color="primary"
      max-width="560"
      :cancel-label="$t('finance.loanApplications.cancel')"
      :confirm-label="$t('finance.loanApplications.send')"
      :confirm-disabled="!requestAdditionalInfoComment.trim() || requestAdditionalInfoSubmitting"
      @cancel="closeRequestAdditionalInfoDialog"
      @confirm="sendRequestAdditionalInfo"
    >
      <p class="request-additional-info-instruction mb-4">
        {{ $t('finance.loanApplications.pleaseProvideSpecificCommentsOrInstructionsInTheTextBoxBelowOnceSubmittedTheFarmerWillBeNotifiedYouWillBeNotifiedWhenTheFarmerUpdatesTheApplication') }}
      </p>
      <v-textarea
        v-model="requestAdditionalInfoComment"
        outlined
        auto-grow
        rows="5"
        :placeholder="$t('finance.loanApplications.enterHere')"
        class="request-additional-info-textarea"
        hide-details
      />
    </ConfirmationDialog>

    <!-- Reject confirmation dialog -->
    <ConfirmationDialog
      v-model="showRejectDialog"
      :title="$t('finance.loanApplications.warning')"
      header-color="error"
      :message="$t('finance.loanApplications.areYouSureYouWantToRejectTheLoanApplication')"
      :confirm-label="$t('finance.loanApplications.confirm')"
      :confirm-disabled="statusActionSubmitting"
      @cancel="closeRejectDialog"
      @confirm="confirmReject"
    />

    <!-- Send to Bank confirmation dialog -->
    <ConfirmationDialog
      v-model="showSendToBankDialog"
      :title="$t('finance.loanApplications.confirmation')"
      header-color="primary"
      :message="$t('finance.loanApplications.areYouSureYouWantToSendTheApplicationToTheBankForApprovalProcess')"
      :confirm-label="$t('finance.loanApplications.confirm')"
      :confirm-disabled="statusActionSubmitting"
      @cancel="closeSendToBankDialog"
      @confirm="confirmSendToBank"
    />

    <!-- Approval Process confirmation dialog (bank user flow: pending / sent_to_bank) -->
    <ConfirmationDialog
      v-model="showApprovalProcessDialog"
      :title="$t('finance.loanApplications.confirmation')"
      header-color="primary"
      :message="approvalConfirmationMessage"
      :cancel-label="$t('finance.loanApplications.cancel')"
      :confirm-label="$t('finance.loanApplications.confirm')"
      @cancel="closeApprovalProcessDialog"
      @confirm="confirmApprovalProcess"
    />
  </div>
</template>

<script>
import moment from 'moment';
import loadingMixin from '@/mixins/LoadingMixin';
import Breadcrumb from '@/components/core/Breadcrumb';
import LocationDialog from './components/LocationDialog.vue';
import ConfirmationDialog from './components/ConfirmationDialog.vue';
import FinalizeContract from './components/FinalizeContract.vue';
import LoanDetails from './components/LoanDetails.vue';
import { parseExcelFile } from '../../../_services/XslxService';
import FinanceServiceV2 from '@/_services/FinanceServiceV2';
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton';

export default {
  name: 'Details',
  components: {
    breadcrumb: Breadcrumb,
    LocationDialog,
    ConfirmationDialog,
    FinalizeContract,
    LoanDetails,
  },
  data() {
    return {
      applicationIdParam: null,
      mapLat: 0,
      mapLng: 0,
      showMapDialog: false,
      showRequestAdditionalInfoDialog: false,
      requestAdditionalInfoComment: '',
      requestAdditionalInfoSubmitting: false,
      statusActionSubmitting: false,
      showRejectDialog: false,
      showSendToBankDialog: false,
      showApprovalProcessDialog: false,
      /** Bank must confirm Approval Process before Finalize Contract / Loan Details are shown (sent_to_bank) */
      approvalProcessStarted: false,
      additionalInfoSectionExpanded: true,
      signInApp: true,
      uploadedContractFile: null,
      mobileUserSignedContracts: [],
      additionalQuestions: [],
      loanDetails: {
        loanAmount: '',
        interestRate: '',
        interestRateUnit: 'per_year',
        numDisbursements: '',
        numInstallments: '',
        gracePeriod: '',
        gracePeriodUnit: 'm',
        gracePeriodType: 'only_interest',
        startDate: '',
        contractS3Link: '',
      },
      interestRateUnitOptions: [
        { label: 'Per Year', value: 'per_year' },
        { label: 'Per Month', value: 'per_month' },
      ],
      gracePeriodUnitOptions: [
        { label: 'Months', value: 'm' },
        { label: 'Days', value: 'd' },
      ],
      gracePeriodTypeOptions: [
        { text: 'Pay Only Interest', value: 'only_interest' },
        { text: 'No Payment', value: 'no_pay' },
        { text: 'Pay Only Principal', value: 'only_principal' },
      ],
      uploadedDisbursementFile: null,
      uploadedRepaymentFile: null,
      disbursementScheduleGenerated: false,
      repaymentScheduleGenerated: false,
      disbursementScheduleData: [],
      repaymentScheduleData: [],
      disbursementSearch: '',
      disbursementStatusFilter: null,
      repaymentSearch: '',
      repaymentStatusFilter: null,
      repaymentSchedulePage: 1,
      repaymentSchedulePerPage: 12,
      scheduleStatusOptions: [
        { text: 'Upcoming', value: 'upcoming' },
        { text: 'Pending', value: 'pending' },
        { text: 'Paid', value: 'paid' },
      ],
      additionalInfoRequests: [],
      farmerUpdates: [],
      breadcrumbs: [
        {
          text: this.$t('finance.loanApplications.financeManagement'),
          disabled: false,
          href: '/finance-management',
          exact: true,
        },
        {
          text: this.$t('finance.loanApplications.loanApplications'),
          disabled: false,
          href: '/finance-management/loan-applications',
        },
        {
          text: this.$t('finance.loanApplications.loanApplicationDetails'),
          disabled: true,
        },
      ],
      application: {
        id: null,
        applicationId: '',
        submissionDate: '',
        status: 'pending',
        programName: '',
        bankName: '',
        firstName: '',
        lastName: '',
        applicantName: '',
        applicantEmail: '',
        phoneNumber: '',
        loanAmountFormatted: '',
        yieldChangePercent: 0,
        farmName: '',
        area: '',
        provinceState: '',
        regionDistrict: '',
        villageTownCity: '',
        address: '',
        farmLocation: '',
      },
      activitySearch: '',
      activityRoleFilter: null,
      activityRoleOptions: [
        { text: 'All', value: null },
        { text: 'Bank', value: 'Bank' },
        { text: 'Cooperative', value: 'Cooperative' },
        { text: 'Farmer', value: 'Farmer' },
        { text: 'System', value: 'System' },
      ],
      activityLog: [],
      farmPreviewMapInstance: null,
      farmPreviewMarker: null,
    };
  },
  computed: {
    hasFarmMapCoords() {
      const lat = Number(this.mapLat);
      const lng = Number(this.mapLng);
      return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        !(lat === 0 && lng === 0)
      );
    },
    additionalInfoEntries() {
      const entries = [];
      (this.additionalInfoRequests || []).forEach((r) => {
        entries.push({
          type: 'request',
          title: this.$t('finance.loanApplications.additionalInformationRequested'),
          date: r.date,
          message: r.message,
          createdAt: r.createdAt || r.date,
        });
      });
      (this.farmerUpdates || []).forEach((u) => {
        entries.push({
          type: 'farmer_update',
          title: this.$t('finance.loanApplications.farmerUpdates'),
          date: u.date,
          message: u.message,
          createdAt: u.createdAt || u.date,
        });
      });
      entries.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'request' ? -1 : 1;
        const dA = new Date(a.createdAt || a.date).getTime();
        const dB = new Date(b.createdAt || b.date).getTime();
        return dB - dA;
      });
      return entries;
    },
    mapCenter() {
      return {
        lat: Number(this.mapLat) || 0,
        lng: Number(this.mapLng) || 0,
      };
    },
    yieldChange() {
      return this.application.yieldChangePercent || 0;
    },
    yieldChangeIcon() {
      return this.yieldChange >= 0 ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold';
    },
    yieldChangeColor() {
      return this.yieldChange >= 0 ? '#00BD73' : 'red';
    },
    yieldChangeDisplay() {
      const absVal = Math.abs(this.yieldChange);
      return `${absVal} %`;
    },
    activityLogHeaders() {
      return [
        { text: this.$t('finance.loanApplications.user'), value: 'user' },
        { text: this.$t('finance.loanApplications.userRole'), value: 'role' },
        { text: this.$t('finance.loanApplications.ipAddress'), value: 'ip' },
        { text: this.$t('finance.loanApplications.dateAndTime'), value: 'dateTime' },
        { text: this.$t('finance.loanApplications.description'), value: 'description' },
        { text: this.$t('finance.loanApplications.activityDescription'), value: 'activityDescription' },
      ];
    },
    filteredActivityLog() {
      let items = [...this.activityLog];

      if (this.activityRoleFilter) {
        items = items.filter((i) => i.role === this.activityRoleFilter);
      }

      if (this.activitySearch) {
        const s = this.activitySearch.toLowerCase();
        items = items.filter(
          (i) =>
            i.user.toLowerCase().includes(s) ||
            i.role.toLowerCase().includes(s) ||
            i.description.toLowerCase().includes(s) ||
            i.activityDescription.toLowerCase().includes(s),
        );
      }

      return items;
    },
    statusText() {
      if (this.isBankUser && this.application.status === 'sent_to_bank') return this.$t('finance.loanApplications.pending');
      switch (this.application.status) {
        case 'pending':
          return this.$t('finance.loanApplications.pending');
        case 'waiting_signatures':
          return this.$t('finance.loanApplications.waitingSignatures');
        case 'sent_to_bank':
          return this.$t('finance.loanApplications.sentToBank');
        case 'approved':
          return this.$t('finance.loanApplications.approved');
        case 'rejected':
          return this.$t('finance.loanApplications.rejected');
        default:
          return this.application.status || 'N/A';
      }
    },
    /** Status pill class: match List.vue (approved = green, others same as list) */
    detailsStatusClass() {
      const status = this.application.status;
      if (this.isBankUser && status === 'sent_to_bank') return 'status-pending';
      switch (status) {
        case 'pending':
          return 'status-pending';
        case 'waiting_signatures':
          return 'status-waiting-signatures';
        case 'sent_to_bank':
          return 'status-sent-to-bank';
        case 'approved':
          return 'status-approved';
        case 'rejected':
          return 'status-rejected';
        default:
          return 'status-pending';
      }
    },
    /** Show Additional Information card when any additional-info request exists (placed just below Farm Reports) */
    showAdditionalInfoCard() {
      return this.additionalInfoRequests && this.additionalInfoRequests.length > 0;
    },
    additionalInfoRequestForCard() {
      const requests = this.additionalInfoRequests || [];
      if (!requests.length) return null;
      return [...requests].sort(
        (a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date),
      )[0];
    },
    farmerResponseForCard() {
      const updates = this.farmerUpdates || [];
      if (!updates.length) return null;
      return [...updates].sort(
        (a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date),
      )[0];
    },
    additionalInfoRequestDisplayDateTime() {
      const r = this.additionalInfoRequestForCard;
      if (!r) return '—';
      return r.time ? `${r.date} | ${r.time}` : r.date;
    },
    farmerResponseDisplayDateTime() {
      const r = this.farmerResponseForCard;
      if (!r) return '—';
      return r.time ? `${r.date} | ${r.time}` : r.date;
    },
    farmerResponseAttachments() {
      const r = this.farmerResponseForCard;
      return (r && r.attachments) ? r.attachments : [];
    },
    isBankUser() {
      return this.checkRole('bank_admin');
    },
    isFinanceAdmin() {
      return this.checkRole('finance_admin');
    },
    /** Bank can request additional info, reject, or start approval while pending, sent_to_bank, or waiting_signatures. */
    bankEligibleStatusForActions() {
      return ['pending', 'sent_to_bank', 'waiting_signatures'].includes(this.application.status);
    },
    approvalActionButtonText() {
      return this.approvalProcessStarted
        ? this.$t('finance.loanApplications.approve')
        : this.$t('finance.loanApplications.approvalProcess');
    },
    approvalConfirmationMessage() {
      return this.approvalProcessStarted
        ? this.$t('finance.loanApplications.areYouSureYouWantToApproveTheLoanApplication')
        : this.$t('finance.loanApplications.areYouSureYouWantToStartTheApprovalProcess');
    },
    /** From approval URL → details; from details → list */
    backButtonTo() {
      const id = this.applicationIdParam || this.$route.params.id;
      if (this.$route.name === 'loanApplicationApproval') {
        return { name: 'loanApplicationDetails', params: { id: String(id) } };
      }
      return { name: 'loanApplications' };
    },
    /**
     * Flow: Finance admin reviews (pending) → send to bank sets sent_to_bank → bank runs Approval Process and
     * fills contract/loan details → status moves to waiting_signatures for farmer signatures → approved / rejected.
     */
    /** Bank only: Finalize Contract + Loan Details after confirming Approval Process (status sent_to_bank). */
    showSentToBankSections() {
      return (
        this.application.status === 'sent_to_bank' &&
        this.isBankUser &&
        this.approvalProcessStarted
      );
    },
    /** Summary-only loan details while waiting for signatures (not sent_to_bank bank-editing phase). */
    showWaitingSignaturesView() {
      return this.application.status === 'waiting_signatures';
    },
    showTopLoanAmount() {
      return (
        this.approvalProcessStarted ||
        ['sent_to_bank', 'waiting_signatures', 'approved'].includes(this.application.status)
      );
    },
    /** When mobile users have signed, status becomes approved; show same contract view (bank + signed by applicant) for approved */
    showContractFromBankAndApplicantView() {
      return (
        this.application.status === 'waiting_signatures' ||
        this.application.status === 'approved'
      );
    },
    /** Finalize Contract: bank user after approval confirmation while sent_to_bank */
    showFinalizeContract() {
      return this.showSentToBankSections;
    },
    /** Bank can edit only after confirming Approval Process (sent_to_bank) */
    canBankEditDetails() {
      return this.isBankUser && this.application.status === 'sent_to_bank' && this.approvalProcessStarted;
    },
    /** Finance admin (cooperative) can edit Loan Details while reviewing (pending only). */
    canFinanceAdminEditDetails() {
      return !this.isBankUser && this.application.status === 'pending';
    },
    /** Finance admin must not see Contract or Loan Details while pending (review) or sent_to_bank (with bank). */
    hideContractAndLoanDetailsForFinanceAdmin() {
      return (
        !this.isBankUser &&
        ['pending', 'sent_to_bank'].includes(this.application.status)
      );
    },
    /** Loan details form: bank after approval on sent_to_bank, or waiting_signatures (summary) / approved; hidden for finance admin when pending or sent_to_bank. */
    showLoanDetailsFormCard() {
      if (this.hideContractAndLoanDetailsForFinanceAdmin) return false;
      return (
        this.showSentToBankSections ||
        this.showWaitingSignaturesView ||
        this.application.status === 'approved'
      );
    },
    /** Loan tables card: when Loan Details visible; approved always shows both tables */
    showLoanDetailsTablesCard() {
      if (this.hideContractAndLoanDetailsForFinanceAdmin) return false;
      const loanDetailsVisible =
        this.showSentToBankSections ||
        this.showWaitingSignaturesView ||
        this.application.status === 'approved';
      if (!loanDetailsVisible) return false;
      if (this.application.status === 'approved') return true;
      return this.disbursementScheduleGenerated || this.repaymentScheduleGenerated;
    },
    /** Loan Details editable by bank when sent_to_bank + approval started, or finance admin when pending */
    canEditLoanDetails() {
      return this.canBankEditDetails || this.canFinanceAdminEditDetails;
    },
    loanDetailsSummary() {
      const principal = this.parseNumeric(this.loanDetails.loanAmount);
      const installments = this.parseNumeric(this.loanDetails.numInstallments);
      const disbursements = this.parseNumeric(this.loanDetails.numDisbursements);
      const gracePeriod = this.parseNumeric(this.loanDetails.gracePeriod);
      const perPeriodRate = this.periodRateFromInputs();

      if (
        !Number.isFinite(principal) ||
        principal <= 0 ||
        !Number.isFinite(installments) ||
        installments <= 0 ||
        !Number.isFinite(perPeriodRate) ||
        perPeriodRate < 0
      ) {
        const total = this.loanDetails.loanAmount || this.application.loanAmountFormatted || '—';
        return {
          totalLoan: total,
          totalInterest: '—',
          totalRepayment: '—',
        };
      }

      const gracePeriods = Number.isFinite(gracePeriod) && gracePeriod > 0 ? gracePeriod : 0;
      const effectiveDisbursementFactor = this.estimateDisbursementInterestFactor(
        Number.isFinite(disbursements) && disbursements > 0 ? disbursements : 1,
      );

      const graceInterest =
        principal * perPeriodRate * gracePeriods * effectiveDisbursementFactor;

      const n = Math.max(1, Math.floor(installments));
      const avgOutstandingPrincipal = principal * (n + 1) / (2 * n);
      const installmentInterest = perPeriodRate === 0
        ? 0
        : avgOutstandingPrincipal * perPeriodRate * n;

      const totalInterestValue = Math.max(0, graceInterest + installmentInterest);
      const totalRepaymentValue = principal + totalInterestValue;

      return {
        totalLoan: this.formatCurrencyLikeLoan(principal),
        totalInterest: this.formatCurrencyLikeLoan(totalInterestValue),
        totalRepayment: this.formatCurrencyLikeLoan(totalRepaymentValue),
      };
    },
    disbursementFileExt() {
      if (!this.uploadedDisbursementFile?.file) return 'XLS';
      const n = (this.uploadedDisbursementFile.file.name || '').split('.').pop();
      return (n || 'XLS').toUpperCase();
    },
    repaymentFileExt() {
      if (!this.uploadedRepaymentFile?.file) return 'XLS';
      const n = (this.uploadedRepaymentFile.file.name || '').split('.').pop();
      return (n || 'XLS').toUpperCase();
    },
    filteredDisbursementSchedule() {
      let items = [...this.disbursementScheduleData];
      if (this.disbursementSearch) {
        const s = this.disbursementSearch.toLowerCase();
        items = items.filter(
          (r) =>
            (r.payment && r.payment.toLowerCase().includes(s)) ||
            (r.disbursedAmount && r.disbursedAmount.toLowerCase().includes(s)) ||
            (r.date && r.date.toLowerCase().includes(s)) ||
            (r.transactionId && r.transactionId.toLowerCase().includes(s)),
        );
      }
      if (this.disbursementStatusFilter) {
        items = items.filter((r) => r.status === this.disbursementStatusFilter);
      }
      return items;
    },
    filteredRepaymentSchedule() {
      let items = [...this.repaymentScheduleData];
      if (this.repaymentSearch) {
        const s = this.repaymentSearch.toLowerCase();
        items = items.filter(
          (r) =>
            (r.installment && r.installment.toLowerCase().includes(s)) ||
            (r.dueDate && r.dueDate.toLowerCase().includes(s)) ||
            (r.principalInterest && r.principalInterest.toLowerCase().includes(s)) ||
            (r.totalPayment && r.totalPayment.toLowerCase().includes(s)),
        );
      }
      if (this.repaymentStatusFilter) {
        items = items.filter((r) => r.status === this.repaymentStatusFilter);
      }
      return items;
    },
    paginatedRepaymentSchedule() {
      const start = (this.repaymentSchedulePage - 1) * this.repaymentSchedulePerPage;
      return this.filteredRepaymentSchedule.slice(start, start + this.repaymentSchedulePerPage);
    },
    repaymentScheduleTotalPages() {
      return Math.ceil(this.filteredRepaymentSchedule.length / this.repaymentSchedulePerPage) || 1;
    },
    disbursementTableHeaders() {
      return [
        { text: this.$t('finance.loanApplications.payments'), value: 'paymentLabel', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.disbursedAmount'), value: 'disbursedAmount', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.date'), value: 'dueDate', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.transactionIdAndDate'), value: 'transactionId', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.status'), value: 'status', sortable: true, class: 'schedule-table-header' },
        { text: this.$t(), value: 'payment', sortable: true, class: 'schedule-table-header' },
        { text: '', value: 'actions', sortable: false, width: 60, class: 'schedule-table-header' },
      ];
    },
    repaymentTableHeaders() {
      return [
        { text: this.$t('finance.loanApplications.installment'), value: 'installmentLabel', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.dueDate'), value: 'dueDate', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.principalAndInterest'), value: 'principalInterest', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.totalPayment'), value: 'totalPayment', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.transactionIdAndDate'), value: 'transactionId', sortable: true, class: 'schedule-table-header' },
        { text: this.$t('finance.loanApplications.status'), value: 'status', sortable: true, class: 'schedule-table-header' },
        { text: '', value: 'actions', sortable: false, width: 60, class: 'schedule-table-header' },
      ];
    },
  },
  methods: {
    checkRole(userRole) {
      const rolesFromStore =
        this.$store?.state?.auth?.user?.user_role_assoc || [];
      if (Array.isArray(rolesFromStore) && rolesFromStore.length > 0) {
        return rolesFromStore.some((role) => role && role.id === userRole);
      }
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const roles = Array.isArray(user?.user_role_assoc)
          ? user.user_role_assoc
          : [];
        return roles.some((role) => role && role.id === userRole);
      } catch (e) {
        return false;
      }
    },
    parseNumeric(value) {
      if (value == null) return NaN;
      const cleaned = String(value).replace(/[^0-9.-]/g, '');
      if (!cleaned) return NaN;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : NaN;
    },
    periodRateFromInputs() {
      const rawRate = this.parseNumeric(this.loanDetails.interestRate);
      if (!Number.isFinite(rawRate) || rawRate < 0) return NaN;
      const decimalRate = rawRate / 100;
      if (this.loanDetails.interestRateUnit === 'per_month') {
        return decimalRate;
      }
      return decimalRate / 12;
    },
    estimateDisbursementInterestFactor(disbursements) {
      if (!Number.isFinite(disbursements) || disbursements <= 1) return 1;
      // Equal-tranche assumption: average outstanding principal while disbursing.
      return (disbursements + 1) / (2 * disbursements);
    },
    formatCurrencyLikeLoan(value) {
      if (!Number.isFinite(value)) return '—';
      const currency = this.application?.loanProgram?.loanCurrency || '';
      const formatted = value.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
      return currency ? `${currency} ${formatted}` : formatted;
    },
    getScheduleStartDate() {
      const raw = this.loanDetails.startDate;
      if (raw) {
        const parsed = moment(raw, ['DD-MMM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        if (parsed.isValid()) return parsed.startOf('day');
      }
      return moment().startOf('day');
    },
    addGracePeriodToDate(baseDate) {
      const grace = this.parseNumeric(this.loanDetails.gracePeriod);
      if (!Number.isFinite(grace) || grace <= 0) return baseDate.clone();
      if (this.loanDetails.gracePeriodUnit === 'd') {
        return baseDate.clone().add(Math.floor(grace), 'days');
      }
      return baseDate.clone().add(Math.floor(grace), 'months');
    },
    formatLoanAmountDisplay(amount, currency) {
      if (amount == null || amount === '') return '';
      const n = Number(amount);
      const formatted = Number.isFinite(n)
        ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : String(amount);
      return currency ? `${currency} ${formatted}` : formatted;
    },
    buildLoanDetailsFromPayload(data) {
      const loanDetails = data.loanDetails;

      if (!loanDetails) {
        this.$notify({
          title: 'Loading Error',
          text: 'Failed to load loan application details',
          type: 'error'
        })
      }

      return {
        loanAmount: loanDetails.loanAmount || '',
        interestRate: loanDetails.interestRate || '',
        interestRateUnit: loanDetails.interestPeriod || 'per_year',
        numDisbursements: loanDetails.numberOfDisbursements?.toString() || '',
        numInstallments: loanDetails.numberOfInstallments?.toString() || '',
        gracePeriod: loanDetails.gracePeriod?.toString() || '',
        gracePeriodUnit: loanDetails.gracePeriodUnit || 'm',
        gracePeriodType: loanDetails.gracePeriodPayment || 'only_interest',
        startDate: loanDetails.startDate || '',
        contractS3Link: loanDetails.contractS3Link || ''
      };
    },
    mapAdditionalInfoRowsFromApi(additionalInfoList) {
      const list = additionalInfoList || [];
      const requests = list.map((info) => ({
        type: 'request',
        id: info.id,
        apiId: info.id,
        createdAt: info.createdAt,
        date: info.createdAt ? moment(info.createdAt).format('DD-MMM-YYYY') : '',
        time: info.createdAt ? moment(info.createdAt).format('hh:mm:ss A') : '',
        message: info.instructions || '',
        attachments: info.attachments || [],
        status: info.status,
      }));
      const responded = list.filter(
        (info) =>
          (info.attachments && info.attachments.length > 0) ||
          ['completed', 'responded', 'fulfilled'].includes(info.status),
      );
      const farmerUpdates = responded.map((info) => ({
        type: 'farmer_update',
        createdAt: info.updatedAt || info.createdAt,
        date: info.updatedAt || info.createdAt
          ? moment(info.updatedAt || info.createdAt).format('DD-MMM-YYYY')
          : '',
        time: info.updatedAt || info.createdAt
          ? moment(info.updatedAt || info.createdAt).format('hh:mm:ss A')
          : '',
        message: info.farmerResponse || info.responseMessage || this.$t('finance.loanApplications.farmerUploadedAttachmentS'),
        attachments: (info.attachments || []).map((a) =>
          typeof a === 'string'
            ? { fileType: 'FILE', url: a }
            : {
                fileType: a.fileType || a.mimeType,
                url: a.url || a.fileUrl || a.signedUrl,
              },
        ),
      }));
      return { requests, farmerUpdates };
    },
    mapApplicationViewFromApi(data) {
      const lp = data.loanProgram || {};
      const applicant = data.applicant || {};
      const farm = data.applicantFarm || {};
      const firstFromApp = [data.applicantFirstName, data.applicantLastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      const applicantName =
        firstFromApp ||
        applicant.fullName ||
        [applicant.firstName, applicant.lastName].filter(Boolean).join(' ').trim() ||
        '—';
      let phoneNumber = data.applicantMobileNumber || '';
      if (!phoneNumber && (applicant.mobile != null || applicant.countryCode != null)) {
        const cc = applicant.countryCode != null ? `+${applicant.countryCode}` : '';
        phoneNumber = [cc, applicant.mobile].filter(Boolean).join(' ').trim();
      }
      const areaDisplay =
        farm.area != null && farm.area !== '' ? String(farm.area) : '—';
      const villageTown = [farm.city, applicant.city, applicant.village].filter(Boolean).join(', ');
      return {
        ...data,
        id: data.id,
        applicationId: data.applicationId || '',
        submissionDate: data.createdAt ? moment(data.createdAt).format('DD-MMM-YYYY') : '',
        status: data.status || 'pending',
        programName: lp.programTitle || '',
        bankName: (lp.bank && lp.bank.name) || '',
        firstName: data.applicantFirstName || applicant.firstName || '',
        lastName: data.applicantLastName || applicant.lastName || '',
        applicantName,
        applicantEmail: data.applicantEmail || applicant.email || '',
        phoneNumber: phoneNumber || '—',
        loanAmountFormatted: this.formatLoanAmountDisplay(data.loanAmount, lp.loanCurrency),
        yieldChangePercent: data.yieldChangePercent || 0,
        farmName: farm.farmName || '—',
        area: areaDisplay,
        provinceState: farm.state || farm.country || '—',
        regionDistrict:
          farm.region || data.applicantPreferredRegion || farm.district || '—',
        villageTownCity: villageTown || '—',
        address: farm.address || applicant.address || '—',
        farmLocation: [farm.state, farm.country].filter(Boolean).join(', '),
      };
    },
    applyFarmMapCoordinates(farm) {
      if (!farm) return false;
      const lat = farm.lat != null ? Number(farm.lat) : null;
      const lngRaw = farm.log != null ? farm.log : farm.lng;
      const lng = lngRaw != null ? Number(lngRaw) : null;
      if (
        lat != null &&
        lng != null &&
        !Number.isNaN(lat) &&
        !Number.isNaN(lng) &&
        lat !== 0 &&
        lng !== 0
      ) {
        this.mapLat = lat;
        this.mapLng = lng;
        return true;
      }
      return false;
    },
    async fallbackMapLocation() {
      if (navigator && navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              this.mapLat = pos.coords.latitude;
              this.mapLng = pos.coords.longitude;
              resolve();
            },
            () => {
              this.mapLat = -6.2088;
              this.mapLng = 106.8456;
              resolve();
            },
          );
        });
      } else {
        this.mapLat = -6.2088;
        this.mapLng = 106.8456;
      }
    },
    async fetchLoanApplication(silent = false) {
      const id = this.applicationIdParam || this.$route.params.id;
      if (id == null || id === '') {
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: this.$t('finance.loanApplications.missingApplicationId'),
          type: 'error',
        });
        return;
      }
      if (!silent) {
        this.startLoading();
      }
      try {
        const response = await FinanceServiceV2.getLoanApplicationById(id);
        const data = response.data;
        if (!data) {
          throw new Error('Empty response');
        }
        this.application = this.mapApplicationViewFromApi(data);
        
        if (this.application.status === 'approved') {
          this.loanDetails = this.buildLoanDetailsFromPayload(data);

          if (data.disbursements.length > 0) {
            this.disbursementScheduleData = data.disbursements;
            this.disbursementScheduleGenerated = true;
          }

          if (data.repayments.length > 0) {
            this.repaymentScheduleData = data.repayments;
            this.repaymentScheduleGenerated = true;
          }
        }
        
        this.additionalQuestions = Array.isArray(data.additionalQuestions)
          ? data.additionalQuestions
          : [];
        const { requests, farmerUpdates } = this.mapAdditionalInfoRowsFromApi(data.additionalInfo);
        this.additionalInfoRequests = requests;
        this.farmerUpdates = farmerUpdates;
        this.mobileUserSignedContracts = [];

        // this.applyPersistedDraft();
        // this.ensureApprovedScheduleTables();

        if (!this.applyFarmMapCoordinates(data.applicantFarm)) {
          await this.fallbackMapLocation();
        }
        await this.$nextTick();
        await this.initOrUpdateFarmPreviewGoogleMap();
      } catch (e) {
        console.error('fetchLoanApplication', e);
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: (e && e.data && e.data.message) || this.$t('finance.loanApplications.couldNotLoadLoanApplication'),
          type: 'error',
        });
      } finally {
        if (!silent) {
          this.stopLoading();
        }
      }
    },
    isAdditionalQuestionAttachment(qa) {
      const fieldType = qa.formField && qa.formField.fieldType;
      if (fieldType === 'attach_file' || fieldType === 'file_upload' || fieldType === 'file') {
        return true;
      }
      const r = qa.response;
      return typeof r === 'string' && /^https?:\/\//i.test(r.trim());
    },
    additionalQuestionFileBadge(url) {
      if (!url || typeof url !== 'string') return 'FILE';
      const path = url.split('?')[0];
      const ext = (path.split('.').pop() || '').toUpperCase();
      return ext && ext.length <= 5 ? ext : 'FILE';
    },
    farmerAttachmentUrl(att) {
      if (!att) return '';
      if (typeof att === 'string') return att;
      return att.url || att.fileUrl || att.signedUrl || '';
    },
    farmerAttachmentBadge(att) {
      const url = this.farmerAttachmentUrl(att);
      if (url) return this.additionalQuestionFileBadge(url);
      return 'PDF';
    },
    destroyFarmPreviewGoogleMap() {
      if (this.farmPreviewMarker) {
        this.farmPreviewMarker.setMap(null);
        this.farmPreviewMarker = null;
      }
      this.farmPreviewMapInstance = null;
    },
    async initOrUpdateFarmPreviewGoogleMap() {
      if (!this.hasFarmMapCoords) {
        this.destroyFarmPreviewGoogleMap();
        return;
      }
      await this.$nextTick();
      const el = this.$refs.farmPreviewMap;
      if (!el) return;
      const lat = Number(this.mapLat);
      const lng = Number(this.mapLng);
      const center = { lat, lng };
      try {
        const loader = getGoogleMapsLoader();
        const google = await loader.load();
        if (!this.farmPreviewMapInstance) {
          this.farmPreviewMapInstance = new google.maps.Map(el, {
            center,
            zoom: 14,
            mapTypeId: 'satellite',
            disableDefaultUI: true,
            zoomControl: true,
          });
          this.farmPreviewMarker = new google.maps.Marker({
            position: center,
            map: this.farmPreviewMapInstance,
          });
        } else {
          this.farmPreviewMapInstance.setCenter(center);
          if (this.farmPreviewMarker) {
            this.farmPreviewMarker.setPosition(center);
          } else {
            this.farmPreviewMarker = new google.maps.Marker({
              position: center,
              map: this.farmPreviewMapInstance,
            });
          }
        }
      } catch (e) {
        console.error('initOrUpdateFarmPreviewGoogleMap', e);
      }
    },
    openFarmReport(reportType) {
      const id =
        this.applicationIdParam ||
        this.application?.applicationId ||
        this.application?.id;

      this.$router
        .push({
          name: 'loanApplicationFarmReports',
          params: { id },
          query: { report: reportType },
        })
        .catch(() => {});
    },

    openYieldPredictionReport() {
      this.openFarmReport('yield');
    },
    closeRequestAdditionalInfoDialog() {
      this.showRequestAdditionalInfoDialog = false;
      this.requestAdditionalInfoComment = '';
    },
    closeRejectDialog() {
      this.showRejectDialog = false;
    },
    async confirmReject() {
      this.startLoading();
      const rawId = this.applicationIdParam || this.application?.id || this.$route.params.id;
      const id = Number(rawId);
      if (rawId == null || rawId === '' || Number.isNaN(id)) {
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: this.$t('finance.loanApplications.missingApplicationId'),
          type: 'error',
        });
        return;
      }
      this.statusActionSubmitting = true;
      try {
        await FinanceServiceV2.patchLoanApplicationStatus(id, { status: 'rejected' });
        this.$notify({
          title: this.$t('finance.loanApplications.success'),
          text: this.$t('finance.loanApplications.loanApplicationHasBeenRejected'),
          type: 'success',
        });
        this.closeRejectDialog();
        await this.fetchLoanApplication(true);
      } catch (e) {
        console.error('confirmReject', e);
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: (e && e.data && e.data.message) || this.$t('finance.loanApplications.couldNotUpdateApplication'),
          type: 'error',
        });
      } finally {
        this.statusActionSubmitting = false;
        this.stopLoading();
      }
    },
    closeSendToBankDialog() {
      this.showSendToBankDialog = false;
    },
    async confirmSendToBank() {
      this.startLoading();  
      const rawId = this.applicationIdParam || this.application?.id || this.$route.params.id;
      const id = Number(rawId);
      if (rawId == null || rawId === '' || Number.isNaN(id)) {
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: this.$t('finance.loanApplications.missingApplicationId'),
          type: 'error',
        });
        return;
      }
      this.statusActionSubmitting = true;
      try {
        await FinanceServiceV2.patchLoanApplicationStatus(id, { status: 'sent_to_bank' });
        this.$notify({
          title: this.$t('finance.loanApplications.success'),
          text: this.$t('finance.loanApplications.applicationHasBeenSentToTheBankForApproval'),
          type: 'success',
        });
        this.closeSendToBankDialog();
        await this.fetchLoanApplication(true);
      } catch (e) {
        console.error('confirmSendToBank', e);
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: (e && e.data && e.data.message) || this.$t('finance.loanApplications.couldNotUpdateApplication'),
          type: 'error',
        });
      } finally {
        this.statusActionSubmitting = false;
        this.stopLoading();
      }
    },
    closeApprovalProcessDialog() {
      this.showApprovalProcessDialog = false;
    },
    draftStorageKey() {
      const id = this.applicationIdParam || this.$route.params.id || this.application?.id;
      return id ? `finance.loanApplication.${id}.draft` : '';
    },
    buildDraftPayload(statusOverride) {
      const safeFile = (f) => (f && f.name ? { name: f.name } : null);
      return {
        approvalProcessStarted: this.approvalProcessStarted,
        status: statusOverride || this.application.status,
        signInApp: this.signInApp,
        loanDetails: { ...this.loanDetails },
        uploadedContractFile: safeFile(this.uploadedContractFile),
        uploadedDisbursementFile: safeFile(this.uploadedDisbursementFile),
        uploadedRepaymentFile: safeFile(this.uploadedRepaymentFile),
        disbursementScheduleGenerated: this.disbursementScheduleGenerated,
        repaymentScheduleGenerated: this.repaymentScheduleGenerated,
        disbursementScheduleData: Array.isArray(this.disbursementScheduleData)
          ? [...this.disbursementScheduleData]
          : [],
        repaymentScheduleData: Array.isArray(this.repaymentScheduleData)
          ? [...this.repaymentScheduleData]
          : [],
        updatedAt: new Date().toISOString(),
      };
    },
    persistDraft(statusOverride) {
      const key = this.draftStorageKey();
      if (!key) return;
      try {
        localStorage.setItem(key, JSON.stringify(this.buildDraftPayload(statusOverride)));
      } catch (e) {
        console.error('persistDraft', e);
      }
    },
    applyPersistedDraft() {
      const key = this.draftStorageKey();
      if (!key) return;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const draft = JSON.parse(raw);
        if (draft.loanDetails && typeof draft.loanDetails === 'object') {
          this.loanDetails = { ...this.loanDetails, ...draft.loanDetails };
        }
        if (draft.uploadedContractFile) this.uploadedContractFile = draft.uploadedContractFile;
        if (draft.uploadedDisbursementFile) this.uploadedDisbursementFile = draft.uploadedDisbursementFile;
        if (draft.uploadedRepaymentFile) this.uploadedRepaymentFile = draft.uploadedRepaymentFile;
        this.signInApp = typeof draft.signInApp === 'boolean' ? draft.signInApp : this.signInApp;
        this.disbursementScheduleGenerated = !!draft.disbursementScheduleGenerated;
        this.repaymentScheduleGenerated = !!draft.repaymentScheduleGenerated;
        this.disbursementScheduleData = Array.isArray(draft.disbursementScheduleData)
          ? draft.disbursementScheduleData
          : this.disbursementScheduleData;
        this.repaymentScheduleData = Array.isArray(draft.repaymentScheduleData)
          ? draft.repaymentScheduleData
          : this.repaymentScheduleData;
        if (draft.status) this.application.status = draft.status;
        if (typeof draft.approvalProcessStarted === 'boolean') {
          this.approvalProcessStarted = draft.approvalProcessStarted;
        }
      } catch (e) {
        console.error('applyPersistedDraft', e);
      }
    },
    async saveLoanApprovalDetails() {
      try {
        const formData = new FormData();

        if (this.disbursementScheduleData.length === 0 || this.repaymentScheduleData.length === 0) {
          throw new Error("Disbursement or Repayment Schedule Missing!");
        }

        if (this.uploadedContractFile instanceof File) {
          formData.append('contractS3File', this.uploadedContractFile);
        }

        formData.append('applicationId', this.getApplicationId());
        formData.append('contractSigned', this.signInApp);
        formData.append('loanAmount', this.loanDetails.loanAmount);
        formData.append('numberOfDisbursements', this.loanDetails.numDisbursements);
        formData.append('numberOfInstallments', this.loanDetails.numInstallments);
        formData.append('gracePeriod', this.loanDetails.gracePeriod);
        formData.append('gracePeriodUnit', this.loanDetails.gracePeriodUnit);
        formData.append('gracePeriodPayment', this.loanDetails.gracePeriodType);
        formData.append('interestRate', this.loanDetails.interestRate);
        formData.append('interestPeriod', this.loanDetails.interestRateUnit);
        formData.append('startDate', this.loanDetails.startDate);
        formData.append('disbursements', JSON.stringify(this.disbursementScheduleData));
        formData.append('repayments', JSON.stringify(this.repaymentScheduleData));

        // Calling the approval details API
        const response = await FinanceServiceV2.createLoanApprovalDetails(formData);

        if (!response.success) throw new Error();
      } catch (e) {
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: e.message,
          type: 'error'
        });

        throw new Error("Failed to save loan application details");
      }
    },
    async confirmApprovalProcess() {
      if (this.approvalProcessStarted) {
        this.startLoading();
        const id = this.applicationIdParam || this.$route.params.id;
        this.persistDraft('approved');
        if (id) {
          try {
            // Save loan details separately
            await this.saveLoanApprovalDetails();

            const response = await FinanceServiceV2.updateLoanApplication(id, { status: 'approved' });

            if (!response.success) {
              throw new Error(response.message);
            }

            this.application.status = 'approved';
            this.$notify({
              title: this.$t('finance.loanApplications.success'),
              text: this.$t('finance.loanApplications.loanApplicationHasBeenApproved'),
              type: 'success',
            });
            this.closeApprovalProcessDialog();
          } catch (e) {
            this.$notify({
              title: 'Failed to approve application',
              text: e.message,
              type: 'error'
            })
          }
          this.stopLoading();
        }
        
        // this.$router.push({ name: 'loanApplications' }).catch(() => {});
        return;
      }
      // TODO: call API to start approval process when backend is ready
      this.approvalProcessStarted = true;
      if (this.application.status !== 'sent_to_bank') this.application.status = 'sent_to_bank';
      this.$notify({
        title: this.$t('finance.loanApplications.success'),
        text: this.$t('finance.loanApplications.approvalProcessHasBeenStartedYouCanNowFillFinalizeContractAndLoanDetails'),
        type: 'success',
      });
      this.closeApprovalProcessDialog();
      this.$router
        .replace({
          name: 'loanApplicationApproval',
          params: { id: this.applicationIdParam || this.$route.params.id },
        })
        .catch(() => {});
      // Finalize Contract and Loan Details cards now visible for bank user to fill
    },
    buildDisbursementScheduleRowsOrNull() {
      const principal = this.parseNumeric(this.loanDetails.loanAmount);
      let disbursements = this.parseNumeric(this.loanDetails.numDisbursements);
      if (!Number.isFinite(principal) || principal <= 0) return null;
      if (!Number.isFinite(disbursements) || disbursements <= 0) disbursements = 1;
      disbursements = Math.max(1, Math.floor(disbursements));

      const startDate = this.getScheduleStartDate();
      const baseAmount = principal / disbursements;
      const rows = [];
      let allocated = 0;
      for (let i = 1; i <= disbursements; i++) {
        const amount = i === disbursements ? (principal - allocated) : baseAmount;
        allocated += amount;
        rows.push({
          payment: `Payment ${i}`,
          disbursedAmount: this.formatCurrencyLikeLoan(amount),
          date: startDate.clone().add(i - 1, 'months').format('DD-MMM-YYYY'),
          transactionId: '-',
          status: 'upcoming',
        });
      }
      return rows;
    },
    buildRepaymentScheduleRowsOrNull(options = {}) {
      const { relaxValidation = false } = options;
      const principal = this.parseNumeric(this.loanDetails.loanAmount);
      let installmentsRaw = this.parseNumeric(this.loanDetails.numInstallments);
      let perPeriodRate = this.periodRateFromInputs();

      if (!Number.isFinite(principal) || principal <= 0) return null;

      if (!Number.isFinite(installmentsRaw) || installmentsRaw <= 0) {
        if (!relaxValidation) return null;
        installmentsRaw = 1;
      }

      if (!Number.isFinite(perPeriodRate) || perPeriodRate < 0) {
        if (!relaxValidation) return null;
        perPeriodRate = 0;
      }

      const installments = Math.max(1, Math.floor(installmentsRaw));
      const startDate = this.addGracePeriodToDate(this.getScheduleStartDate());
      const principalPart = principal / installments;
      const items = [];
      let outstanding = principal;

      for (let i = 1; i <= installments; i++) {
        const interestAmount = outstanding * perPeriodRate;
        const principalAmount = i === installments ? outstanding : principalPart;
        const totalPayment = principalAmount + interestAmount;
        items.push({
          installment: `Installment ${i}`,
          dueDate: startDate.clone().add(i - 1, 'months').format('DD-MMM-YYYY'),
          principalInterest: this.formatCurrencyLikeLoan(principalAmount + interestAmount),
          totalPayment: this.formatCurrencyLikeLoan(totalPayment),
          transactionId: '-',
          status: 'upcoming',
        });
        outstanding = Math.max(0, outstanding - principalAmount);
      }
      return items;
    },
    ensureApprovedScheduleTables() {
      if (this.application.status !== 'approved') return;
      const needDisb =
        !this.disbursementScheduleGenerated ||
        !Array.isArray(this.disbursementScheduleData) ||
        this.disbursementScheduleData.length === 0;
      if (needDisb) {
        const disbRows = this.buildDisbursementScheduleRowsOrNull();
        if (disbRows && disbRows.length) {
          this.disbursementScheduleData = disbRows;
          this.disbursementScheduleGenerated = true;
        }
      }
      const needRep =
        !this.repaymentScheduleGenerated ||
        !Array.isArray(this.repaymentScheduleData) ||
        this.repaymentScheduleData.length === 0;
      if (needRep) {
        const repRows = this.buildRepaymentScheduleRowsOrNull({ relaxValidation: true });
        if (repRows && repRows.length) {
          this.repaymentScheduleData = repRows;
          this.repaymentScheduleGenerated = true;
        }
      }
    },
    async generateDisbursementSchedule(disbursementFile) {
      const columnMappings = {
        'Disbursement No.': 'paymentLabel',
        'Disbursement Amount': 'disbursedAmount',
        'Disbursement Date': 'dueDate'
      };

      try {
        const rows = await parseExcelFile(disbursementFile.file, columnMappings, { status: 'upcoming' });

        if (!rows || rows.length == 0) {
        this.$notify({
            title: this.$t('Validation'),
            text: this.$t('Please upload a valid disbursement plan file'),
            type: 'warning',
          });
          return;
        }
        this.disbursementScheduleData = rows;
        this.disbursementScheduleGenerated = true;
      } catch (error) {
        this.$notify({
          title: this.$t('finance.loanApplications.validation'),
          text: error.message,
          type: 'warning',
        });
        // this.$notify({
        //   title: this.$t('finance.loanApplications.validation'),
        //   text: this.$t('finance.loanApplications.pleaseEnterAValidLoanAmountToGenerateDisbursementPlan'),
        //   type: 'warning',
        // });
      }
    },
    async generateRepaymentSchedule(repaymentFile) {
      const columnMappings = {
        'Installment No.': 'installmentLabel',
        'Due Date': 'dueDate',
        'Total Payment Amount': 'totalPayment',
        'Principal Amount': 'principalAmount',
        'Interest Amount': 'interestAmount',
      };

      try {
        const rows = await parseExcelFile(repaymentFile.file, columnMappings, { status: 'upcoming' });

        if (!rows || rows.length == 0) {
          this.$notify({
            title: this.$t('Validation'),
            text: this.$t('Please upload a valid repayment plan file'),
            type: 'warning',
          });
          return;
        }
        
        this.repaymentScheduleData = rows;
        this.repaymentScheduleGenerated = true;
      } catch (error) {
        this.$notify({
          title: this.$t('finance.loanApplications.validation'),
          text: error.message,
          type: 'error',
        });
        // this.$notify({
        //   title: this.$t('finance.loanApplications.validation'),
        //   text: this.$t('finance.loanApplications.pleaseEnterAValidLoanAmountToGenerateRepaymentPlan'),
        //   type: 'warning',
        // });
      }
      // if (!Number.isFinite(installmentsRaw) || installmentsRaw <= 0) {
      //   this.$notify({
      //     title: this.$t('finance.loanApplications.validation'),
      //     text: this.$t('finance.loanApplications.pleaseEnterNumberOfInstallmentsToGenerateRepaymentPlan'),
      //     type: 'warning',
      //   });
      //   return;
      // }
      // if (!Number.isFinite(perPeriodRate) || perPeriodRate < 0) {
      //   this.$notify({
      //     title: this.$t('finance.loanApplications.validation'),
      //     text: this.$t('finance.loanApplications.pleaseEnterAValidInterestRateToGenerateRepaymentPlan'),
      //     type: 'warning',
      //   });
      //   return;
      // }

      // const items = this.buildRepaymentScheduleRowsOrNull({ relaxValidation: false });
      // const principal = this.parseNumeric(this.loanDetails.loanAmount);
      // const installmentsRaw = this.parseNumeric(this.loanDetails.numInstallments);
      // const perPeriodRate = this.periodRateFromInputs();

      // if (!Number.isFinite(principal) || principal <= 0) {
      //   this.$notify({
      //     title: this.$t('Validation'),
      //     text: this.$t('Please enter a valid Loan Amount to generate repayment plan.'),
      //     type: 'warning',
      //   });
      //   return;
      // }
      // if (!Number.isFinite(installmentsRaw) || installmentsRaw <= 0) {
      //   this.$notify({
      //     title: this.$t('Validation'),
      //     text: this.$t('Please enter Number of Installments to generate repayment plan.'),
      //     type: 'warning',
      //   });
      //   return;
      // }
      // if (!Number.isFinite(perPeriodRate) || perPeriodRate < 0) {
      //   this.$notify({
      //     title: this.$t('Validation'),
      //     text: this.$t('Please enter a valid Interest Rate to generate repayment plan.'),
      //     type: 'warning',
      //   });
      //   return;
      // }

      // if (!items) return;
    },
    getApplicationId() {
      const rawId = this.applicationIdParam || this.application?.id || this.$route.params.id;
      const applicationId = Number(rawId);
      if (rawId == null || rawId === '' || Number.isNaN(applicationId)) {
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: this.$t('finance.loanApplications.missingApplicationId'),
          type: 'error',
        });
        return;
      }
      return applicationId;
    },
    async sendRequestAdditionalInfo() {
      if (!this.requestAdditionalInfoComment || !this.requestAdditionalInfoComment.trim()) {
        this.$notify({
          title: this.$t('finance.loanApplications.validation'),
          text: this.$t('finance.loanApplications.pleaseEnterYourCommentsOrInstructions'),
          type: 'warning',
        });
        return;
      }
      const applicationId = this.getApplicationId();
      const instructions = this.requestAdditionalInfoComment.trim();
      this.requestAdditionalInfoSubmitting = true;
      try {
        await FinanceServiceV2.createLoanApplicationAdditionalInfo({
          applicationId,
          instructions,
          status: 'pending',
        });
        this.$notify({
          title: this.$t('finance.loanApplications.success'),
          text: this.$t('finance.loanApplications.yourRequestHasBeenSentTheFarmerWillBeNotified'),
          type: 'success',
        });
        this.closeRequestAdditionalInfoDialog();
        await this.fetchLoanApplication(true);
      } catch (e) {
        console.error('sendRequestAdditionalInfo', e);
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: (e && e.data && e.data.message) || this.$t('finance.loanApplications.couldNotSendRequest'),
          type: 'error',
        });
      } finally {
        this.requestAdditionalInfoSubmitting = false;
      }
    },
  },
  async mounted() {
    this.applicationIdParam = this.$route.params.id;
    if (this.$route.path.endsWith('/approval')) {
      this.approvalProcessStarted = true;
      if (this.application.status !== 'sent_to_bank') this.application.status = 'sent_to_bank';
    }
    await this.fetchLoanApplication();
    if (this.$route.query.status) {
      this.application.status = this.$route.query.status;
    }
    if (this.$route.path.endsWith('/approval')) {
      this.approvalProcessStarted = true;
      if (this.application.status !== 'sent_to_bank') this.application.status = 'sent_to_bank';
    }
  },
  beforeDestroy() {
    this.destroyFarmPreviewGoogleMap();
  },
  mixins: [loadingMixin],
};
</script>

<style scoped>
/* Reuse label/value styling from UserDetails.vue */
.view-label {
  font-size: 14px;
  color: #555;
  margin-bottom: 4px;
  display: block;
  font-weight: 400;
}

.view-value {
  font-weight: bold;
  font-size: 1rem;
  color: #333;
}

.view-block {
  margin-bottom: 8px;
}

/* Activity log filter fields: 30% narrower (70% of md=4 width ~23.33%) at md and up */
@media (min-width: 960px) {
  .activity-log-filter-field {
    max-width: 23.33%;
    flex: 0 0 auto;
  }
}

.report-card {
  border-radius: 0;
  border: none;
  padding: 0 8px 0 0;
}

.attachment-card {
  width: 90px;
  height: 115px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 20px;
}

.attachment-icon {
  width: 90px;
  height: 90px;
  background: #e5f8f1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-family: 'Poppins', sans-serif;
}

.attachment-icon-badge {
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.5px;
  color: #135b00;
}

.attachment-download {
  position: absolute;
  top: 4px;
  right: 4px;
  cursor: pointer;
}

.attachment-delete {
  position: absolute;
  bottom: 4px;
  right: 4px;
  cursor: pointer;
}

/* Contract upload preview: download top-left, delete top-right */
.contract-uploaded-card .attachment-download {
  top: 4px;
  left: 4px;
  right: auto;
}

.contract-uploaded-card .attachment-delete {
  top: 4px;
  right: 4px;
  bottom: auto;
}

/* Drop zone (matches carbon-credit Register.vue) */
.dropZone {
  width: 100%;
  max-width: 480px;
  min-height: 120px;
  position: relative;
  border: 2px dashed #97a8b8;
  cursor: pointer;
}

.dropZone:hover {
  border: 2px solid #00bd73;
}

.dropZone-info {
  color: #a8a8a8;
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translate(0, -50%);
  text-align: center;
  font-size: 12px;
}

.dropZone-info .blue-color {
  color: #0057bd;
}

.dropZone-info .underline {
  text-decoration: underline;
}

.dropZone-info .gray-color {
  color: #9e9e9e;
}

.dropZone input {
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}

.dropZone-over {
  background: #5c5c5c;
  opacity: 0.8;
}

.dropZone--compact {
  max-width: 400px;
  min-height: 120px;
  width: 100%;
}

.plan-section-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.plan-generate-btn {
  align-self: flex-start;
}

/* Payment schedule tables - match List.vue design */
.schedule-table-card .loan-list-table ::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: white !important;
  font-weight: bold !important;
}

.schedule-table-card .loan-list-table ::v-deep thead th {
  background-color: #00bd73 !important;
  color: white !important;
}

.schedule-table-card .loan-list-table ::v-deep .v-data-table__wrapper tbody tr {
  cursor: pointer;
}

.schedule-table-card .loan-list-table ::v-deep .v-data-table__wrapper tbody td {
  font-size: 14px !important;
  font-weight: 400 !important;
}

.schedule-table-card .loan-list-table .status-pill-fixed {
  min-width: 180px !important;
  max-width: 240px !important;
  width: 240px !important;
  font-size: 14px !important;
}

.schedule-table-card .loan-list-table .status-pending {
  background-color: #e0e0e0 !important;
  border: 2px solid #9e9e9e !important;
  color: #424242 !important;
}

.loan-summary-box {
  background-color: #f5f5f5;
  border-radius: 4px;
}

/* Unit combined input (matches FarmLocation/PlotLocation hectares-acres design) - selected unit visible */
.unit-combined .unit-input-col {
  flex: 1;
  min-width: 60px;
}

.unit-combined .unit-select-col {
  min-width: 145px;
  flex-shrink: 0;
}

.unit-combined .unit-select {
  min-width: 145px;
}

.unit-combined .unit-select ::v-deep .v-input__slot {
  border: none !important;
  background-color: #00bd73 !important;
  border-radius: 0 4px 4px 0 !important;
  padding-left: 12px !important;
  padding-right: 8px !important;
  min-width: 145px !important;
}

.unit-combined .rounded-r-0 ::v-deep .v-input__slot {
  border-radius: 4px 0 0 4px !important;
}

.unit-combined .unit-select ::v-deep .v-select__selection {
  color: #fff !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: unset !important;
  max-width: none !important;
}

.unit-combined .unit-select ::v-deep .v-input__append-inner .v-icon {
  color: #fff !important;
}

/* Finalize Contract: text size hierarchy and radio layout */
.finalize-contract-text {
  color: #333 !important;
}

.finalize-contract-desc {
  font-size: 13px;
  font-weight: 400;
  color: #333 !important;
}

.finalize-contract-subheading {
  font-size: 16px;
  font-weight: 700;
  color: #333 !important;
}

.finalize-contract-radio-group {
  flex-wrap: wrap;
}

.finalize-contract-radio-group .v-radio {
  margin-right: 24px;
}

.finalize-contract-radio-group ::v-deep .v-input--radio-group__input {
  flex-direction: row;
}

.finalize-contract-radio-group ::v-deep .v-radio .v-label {
  font-size: 13px !important;
  font-weight: 400 !important;
  color: #333 !important;
}

.contract-uploaded-card .attachment-icon {
  position: relative;
}

.attachment-label {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #135b00;
}


.map-caption .map-text {
  font-size: 12px;
  color: #000;
}

.farm-preview-google-map {
  width: 100%;
  height: 180px;
  border-radius: 5px;
  overflow: hidden;
  background: #e8e8e8;
}

.farm-preview-google-map--empty {
  min-height: 180px;
  border: 1px dashed #bdbdbd;
}

.info-status-pill {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 6px 20px;
  min-width: 188px;
  height: 34px;
  background: #e8e8e8;
  border: 2px solid #9e9e9e;
  border-radius: 70px;
}

.info-status-text {
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #424242;
}

/* Status pill colors – match List.vue */
.info-status-pill.status-pending {
  background-color: #e0e0e0 !important;
  border-color: #9e9e9e !important;
}
.info-status-pill.status-pending .info-status-text {
  color: #424242 !important;
}

.info-status-pill.status-waiting-signatures {
  background-color: #fff8e1 !important;
  border-color: #ffc107 !important;
}
.info-status-pill.status-waiting-signatures .info-status-text {
  color: #f57f17 !important;
}

.info-status-pill.status-sent-to-bank {
  background-color: #b3e5fc !important;
  border-color: #0288d1 !important;
}
.info-status-pill.status-sent-to-bank .info-status-text {
  color: #01579b !important;
}

.info-status-pill.status-approved {
  background-color: #c1ffe7 !important;
  border-color: #00bd73 !important;
}
.info-status-pill.status-approved .info-status-text {
  color: #489e16 !important;
}

.info-status-pill.status-rejected {
  background-color: #ffcdd2 !important;
  border-color: #e53935 !important;
}
.info-status-pill.status-rejected .info-status-text {
  color: #b71c1c !important;
}

.status-pill-fixed {
  min-width: 180px !important;
  max-width: 180px !important;
  width: 180px !important;
}

/* Detail header status pill: table cells keep fixed width above; here we need room for i18n */
.info-status-pill.status-pill-fixed {
  width: auto !important;
  max-width: 100% !important;
  min-width: 188px !important;
  height: auto !important;
  min-height: 34px;
}

.info-status-pill.status-pill-fixed .info-status-text {
  white-space: nowrap;
}

/* Additional Information box (NewWarningBox-style, above main v-card) */
.additional-info-card {
  background-color: #fff8e1 !important;
}

.additional-info-header {
  cursor: pointer;
}

.additional-info-heading {
  color: #000;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.additional-info-tooltip-icon {
  color: #0EBF67 !important;
}

.additional-info-body {
  margin: 12px 16px 16px;
}

.additional-info-entry {
  margin-bottom: 12px;
}

.additional-info-entry:last-child {
  margin-bottom: 0;
}

.additional-info-entry-title {
  font-weight: 700;
  font-size: 14px;
  color: #000;
}

.additional-info-entry-text {
  font-weight: 400;
  font-size: 14px;
  color: #000;
  line-height: 1.45;
  margin: 0;
}

/* Request Additional Information dialog - slot content only */
.request-additional-info-instruction {
  font-size: 14px;
  font-weight: 400;
  color: #000;
  line-height: 1.5;
  margin: 0;
}

.request-additional-info-textarea ::v-deep .v-input__slot {
  border-color: #bdbdbd;
  background: #fff;
}
</style>

